import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import * as adminApi from '../../api/admin';
import type {
  DataIntegrity as Report,
  StaleDevice,
  StrandedSession,
} from '../../api/admin';
import { extractErrorMessage } from '../../api/errors';
import * as sessionsApi from '../../api/sessions';
import { RefreshButton } from '../../components/RefreshButton';
import { SortHeader } from '../../components/SortHeader';
import { useSortedRows } from '../../util/tableSort';

/**
 * Data integrity — where recordings have gone missing, grouped by what can
 * actually be done about each kind.
 *
 * The grouping IS the feature. A flat "17 sessions missing" is unactionable:
 * measured on staging, one tenant's 15 absent sessions were 9 that had
 * recorded nothing and would never arrive, and 6 that might still be on a
 * phone. Told apart, one group is a phone to go and open and the other is a
 * capture failure to investigate. Together they are a number nobody can act
 * on — which is how 58 stranded recordings accumulated across three months
 * without anyone noticing.
 *
 * So each section leads with what to DO, and only the section with a
 * deterministic fix gets a button.
 */

function ageLabel(hours: number): string {
  if (hours < 48) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

// Default orders match the API: sessions newest first, devices by the
// number of recordings waiting on them (most first).
const SESSION_SORT = {
  tenant: (r: StrandedSession) => r.tenant_slug,
  participant: (r: StrandedSession) => r.participant_label,
  device: (r: StrandedSession) => r.device_label,
  recorded: (r: StrandedSession) => r.started_at,
  age: (r: StrandedSession) => r.age_hours,
};

const DEVICE_SORT = {
  tenant: (d: StaleDevice) => d.tenant_slug,
  device: (d: StaleDevice) => d.label,
  version: (d: StaleDevice) => d.app_version,
  waiting: (d: StaleDevice) => d.pending_sessions,
  lastSeen: (d: StaleDevice) => d.last_seen_at,
};

function DeviceTable({ rows }: { rows: StaleDevice[] }) {
  const { sort, sorted } = useSortedRows(rows, DEVICE_SORT, 'waiting', 'desc');
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <SortHeader label="Tenant" col="tenant" sort={sort} />
            <SortHeader label="Device" col="device" sort={sort} />
            <SortHeader label="App version" col="version" sort={sort} />
            <SortHeader label="Waiting" col="waiting" sort={sort} align="right" />
            <SortHeader label="Last seen" col="lastSeen" sort={sort} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((d) => (
            <TableRow key={`${d.tenant_slug}-${d.label}-${d.last_seen_at}`} hover>
              <TableCell>{d.tenant_slug}</TableCell>
              <TableCell>{d.label ?? '—'}</TableCell>
              <TableCell>{d.app_version ?? '—'}</TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {d.pending_sessions}
              </TableCell>
              <TableCell>
                {d.last_seen_at
                  ? new Date(d.last_seen_at).toLocaleString()
                  : 'never'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function SessionTable({
  rows,
  onRescore,
  onDisable,
  busyId,
}: {
  rows: StrandedSession[];
  onRescore?: (r: StrandedSession) => void;
  onDisable?: (r: StrandedSession) => void;
  busyId?: string | null;
}) {
  const hasActions = Boolean(onRescore || onDisable);
  const { sort, sorted } = useSortedRows(rows, SESSION_SORT, 'recorded', 'desc');
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1.5 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <SortHeader label="Tenant" col="tenant" sort={sort} />
            <SortHeader label="Participant" col="participant" sort={sort} />
            <SortHeader label="Device" col="device" sort={sort} />
            <SortHeader label="Recorded" col="recorded" sort={sort} />
            <SortHeader label="Age" col="age" sort={sort} align="right" />
            {hasActions && <TableCell align="right">Action</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={`${r.tenant_slug}-${r.session_id}`} hover>
              <TableCell>{r.tenant_slug}</TableCell>
              <TableCell>{r.participant_label ?? '—'}</TableCell>
              <TableCell>{r.device_label ?? '—'}</TableCell>
              <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {new Date(r.started_at).toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {ageLabel(r.age_hours)}
              </TableCell>
              {hasActions && (
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {onRescore && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busyId === r.session_id}
                        onClick={() => onRescore(r)}
                      >
                        {busyId === r.session_id ? 'Re-scoring…' : 'Re-score'}
                      </Button>
                    )}
                    {onDisable && (
                      <Button
                        size="small"
                        color="error"
                        disabled={busyId === r.session_id}
                        onClick={() => onDisable(r)}
                      >
                        Disable
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Section({
  title,
  count,
  guidance,
  children,
}: {
  title: string;
  count: number;
  guidance: string;
  children?: React.ReactNode;
}) {
  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap">
        <Typography variant="h6">{title}</Typography>
        <Chip size="small" label={count} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: '68ch' }}>
        {guidance}
      </Typography>
      {children}
    </Box>
  );
}

export function DataIntegrity({
  embedded = false,
  tenantId,
  allowDisable = false,
}: {
  embedded?: boolean;
  tenantId?: string;
  allowDisable?: boolean;
} = {}) {
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Confirm before disabling. Reversible, but it removes a row from a list
  // someone is working through, and the activity screen asks too — an action
  // that behaves differently in two places is one people stop trusting.
  const [disableTarget, setDisableTarget] = useState<StrandedSession | null>(
    null,
  );
  // Whether Disable is offered. Taken as a PROP rather than read from auth
  // context here: this panel is embedded in two places, and a component that
  // reaches for a provider is one its callers must remember to supply. The
  // tenant page computes it with canWriteTenant — an operational write, so a
  // coordinator qualifies and a read-only tenant_admin does not, matching
  // the server's require_tenant_operator_write.
  const canDisable = Boolean(tenantId) && allowDisable;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // One tenant, or the whole platform. The scope is decided by WHICH
      // endpoint is called, not by a filter the client applies — the
      // server enforces the tenant boundary and a client-side filter
      // would be security theatre over a cross-tenant payload.
      setReport(
        tenantId
          ? await adminApi.getTenantDataIntegrity(tenantId)
          : await adminApi.getDataIntegrity(),
      );
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rescore = useCallback(
    async (r: StrandedSession) => {
      setBusyId(r.session_id);
      setNotice(null);
      setError(null);
      try {
        const res = await adminApi.rescoreSession(r.tenant_id, r.session_id);
        setNotice(
          res.enqueued.length > 0
            ? `Re-score queued for ${r.participant_label ?? r.session_id} ` +
              `(${res.enqueued.join(', ')}). It will appear once scoring finishes.`
            : 'Already being scored — nothing further to do.',
        );
        await load();
      } catch (e) {
        setError(extractErrorMessage(e));
      } finally {
        setBusyId(null);
      }
    },
    [load],
  );

  // Above the early return below: hooks must run in the same order on
  // every render, and a useCallback after a conditional return is only
  // reached once the component has data.
  const confirmDisable = useCallback(async () => {
    const r = disableTarget;
    if (!r) return;
    setBusyId(r.session_id);
    setNotice(null);
    setError(null);
    try {
      // The ROW id, not session_id — different keys, and the wrong one 404s.
      await sessionsApi.disableCaptureSession(r.tenant_id, r.row_id);
      setNotice(
        `Disabled ${r.participant_label ?? r.session_id}. It is excluded from ` +
          'scores and no longer listed here; restore it from Activities.',
      );
      setDisableTarget(null);
      await load();
    } catch (e) {
      setError(extractErrorMessage(e));
      setDisableTarget(null);
    } finally {
      setBusyId(null);
    }
  }, [disableTarget, load]);

  if (loading && report === null) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {!embedded && <Typography variant="h4">Data integrity</Typography>}
        <Box sx={{ ml: 'auto' }}>
          <RefreshButton onClick={() => void load()} refreshing={loading} />
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {report && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {report.recoverable === 0
              ? tenantId
                ? 'Nothing to act on.'
                : `Nothing to act on across ${report.tenants_scanned} tenant(s).`
              : tenantId
                ? `${report.recoverable} recording(s) can be acted on now.`
                : `${report.recoverable} recording(s) can be acted on now, across ` +
                  `${report.tenants_scanned} tenant(s).`}
          </Typography>

          <Section
            title="In storage, but not showing"
            count={report.landed_unscored.length}
            guidance="The file arrived; scoring never ran or never finished. This is the one
              case with a definite fix — re-scoring makes the session appear. Safe to press
              twice; a job already running is not duplicated."
          >
            {report.landed_unscored.length > 0 && (
              <SessionTable
                rows={report.landed_unscored}
                // Re-score is a platform action — the endpoint behind it is
                // superadmin-only. A tenant sees the finding and who to ask;
                // showing a button that 403s would be worse than none.
                onRescore={tenantId ? undefined : (r) => void rescore(r)}
                onDisable={canDisable ? (r) => setDisableTarget(r) : undefined}
                busyId={busyId}
              />
            )}
          </Section>

          <Section
            title="Never reached storage — recent"
            count={report.never_uploaded_recent.length}
            guidance="These finished on a capture device and have not uploaded. Open the app
              on that phone, on wifi, and the queue drains by itself. Do it soon: reinstalling
              the app clears the device's local queue, and after that the recording is gone."
          >
            {report.never_uploaded_recent.length > 0 && (
              <SessionTable
                rows={report.never_uploaded_recent}
                onDisable={canDisable ? (r) => setDisableTarget(r) : undefined}
                busyId={busyId}
              />
            )}
          </Section>

          <Section
            title="Devices to find"
            count={report.stale_devices.length}
            guidance="Phones holding recordings that have not checked in. This is usually the
              fastest fix — one person, one phone, thirty seconds."
          >
            {report.stale_devices.length > 0 && (
              <DeviceTable rows={report.stale_devices} />
            )}
          </Section>

          <Section
            title="Recorded no data"
            count={report.empty_recordings.length}
            guidance="The capture ran and produced nothing, so no upload will ever arrive.
              Listed so they stop being counted as recordings still on their way — there is
              nothing here to recover, and the thing to investigate is the capture itself."
          >
            {report.empty_recordings.length > 0 && (
              <SessionTable
                rows={report.empty_recordings}
                onDisable={canDisable ? (r) => setDisableTarget(r) : undefined}
                busyId={busyId}
              />
            )}
          </Section>

          <Section
            title="Never reached storage — older"
            count={report.never_uploaded_aged.length}
            guidance="Old enough that we cannot tell from here whether the file still exists:
              that depends on whether the app was upgraded in place or reinstalled, and
              whether the device was wiped or reassigned. Shown for completeness. These are
              deliberately excluded from the counts and from alerting, so they do not become
              a backlog nobody can ever clear."
          >
            {report.never_uploaded_aged.length > 0 && (
              <SessionTable
                rows={report.never_uploaded_aged}
                onDisable={canDisable ? (r) => setDisableTarget(r) : undefined}
                busyId={busyId}
              />
            )}
          </Section>
        </>
      )}

      <Dialog
        open={disableTarget !== null}
        onClose={() => (busyId ? null : setDisableTarget(null))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Disable this recording?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It will be excluded from scores and faculty comparisons, and it
            leaves this list — nothing is deleted. It stays on the Activities
            timeline, dimmed, and you can restore it there at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDisableTarget(null)}
            disabled={busyId !== null}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDisable()}
            color="error"
            variant="contained"
            disabled={busyId !== null}
          >
            {busyId ? 'Disabling…' : 'Disable'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
